import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiKeyService } from '../../services/apiKeyService';
import { format } from 'date-fns';

const ApiKeyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [key, setKey] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logsLoading, setLogsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [logsError, setLogsError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [logsPerPage, setLogsPerPage] = useState(20);

    // Search filters
    const [searchFilters, setSearchFilters] = useState({
        endpoint: '',
        method: '',
        ip: '',
        dateFrom: '',
        dateTo: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        loadKey();
    }, [id]);

    useEffect(() => {
        loadLogs();
    }, [id, currentPage, logsPerPage, searchFilters]);

    const loadKey = async () => {
        try {
            setLoading(true);
            const response = await apiKeyService.getApiKey(id);
            setKey(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load API key: ' + (err.message || 'Unknown error'));
            console.error('Error loading API key:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadLogs = async () => {
        try {
            setLogsLoading(true);
            const params = {
                page: currentPage,
                limit: logsPerPage,
                endpoint: searchFilters.endpoint || undefined,
                method: searchFilters.method || undefined,
                ip: searchFilters.ip || undefined,
                dateFrom: searchFilters.dateFrom || undefined,
                dateTo: searchFilters.dateTo || undefined
            };

            // Remove empty filters
            Object.keys(params).forEach(key =>
                params[key] === undefined && delete params[key]
            );

            console.log('API request params:', params);

            const response = await apiKeyService.getApiKeyLogs(id, params);
            setLogs(response.data.logs || []);
            setTotalPages(response.data.pagination?.pages || 1);
            setLogsError(null);
        } catch (err) {
            setLogsError('Failed to load API key logs: ' + (err.message || 'Unknown error'));
            console.error('Error loading API key logs:', err);
        } finally {
            setLogsLoading(false);
        }
    };

    const handleRevokeKey = async () => {
        if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone and will prevent any further usage of this key.')) {
            return;
        }

        try {
            await apiKeyService.revokeApiKey(id);
            alert('API key revoked successfully');
            // Refresh key data
            loadKey();
        } catch (err) {
            setError('Failed to revoke API key: ' + (err.message || 'Unknown error'));
            console.error('Error revoking API key:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setSearchFilters({
            ...searchFilters,
            [name]: value
        });
        setCurrentPage(1); // Reset to first page when filters change
    };

    const resetFilters = () => {
        setSearchFilters({
            endpoint: '',
            method: '',
            ip: '',
            dateFrom: '',
            dateTo: ''
        });
        setCurrentPage(1);
    };

    if (loading) {
        return <div className="p-4">Loading API key details...</div>;
    }

    if (error) {
        return (
            <div className="p-4 text-red-600">
                <p>{error}</p>
                <div className="mt-4">
                    <button
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => navigate('/api-keys')}
                    >
                        Back to API Keys
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">API Key Details</h2>
                <div>
                    <button
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mr-2"
                        onClick={() => navigate(`/api-keys/${id}/edit`)}
                    >
                        Edit
                    </button>
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={handleRevokeKey}
                        disabled={!key.isActive}
                    >
                        {key.isActive ? 'Revoke Key' : 'Key Revoked'}
                    </button>
                </div>
            </div>

            <div className="bg-white p-6 rounded shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-3">Key Information</h3>
                        <div className="space-y-2">
                            <div>
                                <span className="font-medium">Name:</span> {key.name}
                            </div>
                            <div>
                                <span className="font-medium">Status:</span>{' '}
                                <span className={`px-2 py-1 rounded-full text-xs ${key.isActive ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                    }`}>
                                    {key.isActive ? 'Active' : 'Revoked'}
                                </span>
                            </div>
                            <div>
                                <span className="font-medium">User:</span>{' '}
                                {key.userId?.username || key.userId}
                                {key.userId?.email && ` (${key.userId.email})`}
                            </div>
                            <div>
                                <span className="font-medium">Permissions:</span>{' '}
                                {key.permissions?.join(', ') || 'None'}
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-3">Usage Information</h3>
                        <div className="space-y-2">
                            <div>
                                <span className="font-medium">Created:</span> {formatDate(key.createdAt)}
                            </div>
                            <div>
                                <span className="font-medium">Last Used:</span> {formatDate(key.lastUsed)}
                            </div>
                            <div>
                                <span className="font-medium">Expires:</span>{' '}
                                {key.expiresAt ? formatDate(key.expiresAt) : 'Never'}
                            </div>
                            <div>
                                <span className="font-medium">Last Updated:</span> {formatDate(key.updatedAt)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold">Usage Logs</h3>
                    <button
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>
                </div>

                {showFilters && (
                    <div className="bg-gray-50 p-4 rounded mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint</label>
                                <input
                                    type="text"
                                    name="endpoint"
                                    value={searchFilters.endpoint}
                                    onChange={handleFilterChange}
                                    placeholder="e.g. upload-media"
                                    className="w-full px-3 py-2 border rounded text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                                <select
                                    name="method"
                                    value={searchFilters.method}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border rounded text-sm"
                                >
                                    <option value="">All Methods</option>
                                    <option value="GET">GET</option>
                                    <option value="POST">POST</option>
                                    <option value="PUT">PUT</option>
                                    <option value="DELETE">DELETE</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                                <input
                                    type="text"
                                    name="ip"
                                    value={searchFilters.ip}
                                    onChange={handleFilterChange}
                                    placeholder="e.g. 192.168"
                                    className="w-full px-3 py-2 border rounded text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                                <input
                                    type="date"
                                    name="dateFrom"
                                    value={searchFilters.dateFrom}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border rounded text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                                <input
                                    type="date"
                                    name="dateTo"
                                    value={searchFilters.dateTo}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border rounded text-sm"
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                                    onClick={resetFilters}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {logsError && (
                    <div className="bg-red-100 border border-red-300 text-red-600 p-3 rounded mb-4">
                        {logsError}
                    </div>
                )}

                {logsLoading ? (
                    <div className="text-gray-500">Loading logs...</div>
                ) : logs.length === 0 ? (
                    <div className="bg-gray-100 p-4 rounded text-gray-600">
                        No usage logs found for this API key.
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white">
                                <thead>
                                    <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                                        <th className="py-3 px-6 text-left">Timestamp</th>
                                        <th className="py-3 px-6 text-left">Endpoint</th>
                                        <th className="py-3 px-6 text-left">Method</th>
                                        <th className="py-3 px-6 text-left">IP Address</th>
                                        <th className="py-3 px-6 text-left">User Agent</th>
                                        <th className="py-3 px-6 text-left">Status</th>
                                        <th className="py-3 px-6 text-left">Response Time</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-600 text-sm">
                                    {logs.map((log) => (
                                        <tr key={log._id} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="py-3 px-6 text-left whitespace-nowrap">
                                                {formatDate(log.createdAt)}
                                            </td>
                                            <td className="py-3 px-6 text-left">{log.endpoint}</td>
                                            <td className="py-3 px-6 text-left">{log.method}</td>
                                            <td className="py-3 px-6 text-left">{log.ipAddress}</td>
                                            <td className="py-3 px-6 text-left">{log.userAgent || 'Unknown'}</td>
                                            <td className="py-3 px-6 text-left">
                                                <span className={`px-2 py-1 rounded-full text-xs ${log.responseStatus >= 200 && log.responseStatus < 300
                                                    ? 'bg-green-200 text-green-800'
                                                    : 'bg-red-200 text-red-800'
                                                    }`}>
                                                    {log.responseStatus}
                                                </span>
                                            </td>
                                            <td className="py-3 px-6 text-left">{log.responseTime} ms</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex justify-between items-center mt-4">
                            <div>
                                <select
                                    className="border rounded px-2 py-1"
                                    value={logsPerPage}
                                    onChange={(e) => {
                                        setLogsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="10">10 per page</option>
                                    <option value="20">20 per page</option>
                                    <option value="50">50 per page</option>
                                    <option value="100">100 per page</option>
                                </select>
                            </div>

                            <div className="flex items-center">
                                <button
                                    className="px-3 py-1 border rounded mr-2 disabled:opacity-50"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <span className="mx-2">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    className="px-3 py-1 border rounded ml-2 disabled:opacity-50"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ApiKeyDetails; 